<template>
  <h5 class="mb-3">{{ name }}</h5>

  <div class="form-floating mb-4">
    <currency-input
      ref="uiStartingIncome"
      description="annual starting income"
      elementId="startingIncome"
      @entered="onSaveShortcut"
    />
  </div>

  <p class="my-3 text-danger" v-show="showValidation">
    <i class="bi bi-x-circle-fill"></i>
    Current value not valid.
  </p>

  <div class="row">
    <month-and-year
      ref="payableFrom"
      monthLabel="payable from month"
      yearLabel="year"
      ariaMonthLabel="Payable from month"
      ariaYearLabel="Payable from year"
      :defaultYearRange="40"
    />
  </div>

  <div>
    <inflation ref="inflation" />
  </div>

  <bad-response-viewer ref="badResponseViewer" />

  <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
    <lock-button :text="buttonText" :async-action="onSaveClicked"></lock-button>

    <button
      type="button"
      class="btn btn-secondary me-md-2"
      aria-label="Close"
      v-on:click="onCancelClicked"
    >
      Cancel
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, provide } from "vue";
import { inputValidator } from "@/db/ts/inputValidator";
import { FinalSalary } from "@/guiide/ts/finalSalary";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import CurrencyInput from "../../../shared/components/CurrencyInput.vue";
import MonthAndYear from "../common/MonthAndYear.vue";
import Inflation from "../common/Inflation.vue";
import LockButton from "@/guiide/components/common/LockButton.vue";
import BadResponseViewer from "@/shared/components/BadResponseViewer.vue";
import { formatter } from "../../../db/ts/formatter";
import ResponseTracker from "@/shared/ts/responseTracker";
import { app } from "../../ts/app";
import { AxiosResponse } from "axios";

export default defineComponent({
  name: "FinalSalaryEditor",
  emits: ["edit-finished"],
  components: {
    CurrencyInput,
    MonthAndYear,
    Inflation,
    LockButton,
    BadResponseViewer
  },
  setup() {
    const uiStartingIncome = ref(null);
    const flagInvalidAnswer = ref(false);
    const payableFrom = ref(null);
    const inflation = ref(null);
    const badResponseViewer = ref(null);
    const execute = ref(false);

    provide('execute', execute);

    return {
      uiStartingIncome,
      flagInvalidAnswer,
      payableFrom,
      inflation,
      badResponseViewer,
      execute
    };
  },
  data() {
    return {
      inputValidator,
      formatter,
      finalSalary: null as FinalSalary,
      name: "",
      responseTracker: ResponseTracker.empty<AxiosResponse<unknown>>()
    };
  },
  computed: {
    showValidation: function() {
      return this.flagInvalidAnswer;
    },
    buttonText: function(): string {
      return (this.finalSalary?.id ?? Constants.NewId) === Constants.NewId
        ? "Add"
        : "Update";
    }
  },
  methods: {
    setValues(finalSalary: FinalSalary, trdYear: number) {
      this.badResponseViewer.clear();
      this.finalSalary = finalSalary;
      this.name = finalSalary.name;
      this.uiStartingIncome.setValue(
        finalSalary.startingIncome == 0 ? null : finalSalary.startingIncome
      );
      this.payableFrom.setFirstYear(trdYear);
      this.payableFrom.setSelectedValue(
        finalSalary.payableFromMonth.toString(),
        finalSalary.payableFromYear.toString()
      );
      this.inflation.setSelectedValue(finalSalary.inflation);
    },
    onCancelClicked: function() {
      this.editIsFinished();
    },
    onSaveShortcut: async function() {
      this.execute = true;
    },
    onSaveClicked: async function() {
      if (!this.uiStartingIncome.validate()) {
        this.execute = false;
        return;
      }

      const update = ObjectBuilder.createFinalSalary(
        this.finalSalary.id,
        this.finalSalary.name,
        this.uiStartingIncome.getValue(),
        this.payableFrom.getSelectedMonth(),
        this.payableFrom.getSelectedYear(),
        this.inflation.getSelectedValue()
      );

      const responseTracker = await app.updateFinalSalary(update);
      this.execute = false;

      if (responseTracker.isOk) {
        this.editIsFinished();
      } else {
        if (responseTracker.trackerWillHandleFailure) {
          responseTracker.handleFailure();
        } else {
          this.badResponseViewer.update(
            responseTracker.isOk,
            responseTracker.message
          );
        }
      }
    },
    editIsFinished() {
      this.uiStartingIncome.hideAnyWarnings();
      this.$emit("edit-finished");
    }
  }
});
</script>

<style>

</style>
